import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

/* ----------------------- Community Partners view -----------------------
 * Admin CRUD for the caregiver-wellness directory at
 * /resources/community-partners. Categories: spas, gyms, mental wellness,
 * virtual doctors, meal services, respite, support groups, faith, other.
 * Statuses: coming_soon / live / draft (drafts are admin-only).
 */
const PARTNER_BLANK = {
    name: "", category: "mental_wellness", status: "coming_soon",
    country: "US",
    region: "", description: "", url: "", perk: "",
};

export default function CommunityPartnersView() {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ categories: [], statuses: [] });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // partner being edited (or PARTNER_BLANK for new)
    const [subs, setSubs] = useState([]);

    const loadSubs = () => api.get("/admin/marketplace/submissions")
        .then(r => setSubs(r.data.submissions || [])).catch(() => setSubs([]));
    useEffect(() => { loadSubs(); }, []);

    const review = async (id, action) => {
        try {
            await api.post(`/admin/marketplace/submissions/${id}/${action}`,
                action === "reject" ? { reason: "" } : {});
            toast.success(action === "approve" ? "Approved — now live in Care For You." : "Rejected.");
            loadSubs();
            if (action === "approve") load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Review failed.");
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [list, m] = await Promise.all([
                api.get("/admin/community-partners"),
                api.get("/community-partners/_meta"),
            ]);
            setItems(list.data || []);
            setMeta(m.data || { categories: [], statuses: [] });
        } catch (e) {
            toast.error("Couldn't load community partners.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (partner) => {
        try {
            if (partner.id) {
                const { id, created_at, created_by, updated_at, ...patch } = partner;
                await api.patch(`/admin/community-partners/${id}`, patch);
                toast.success("Saved.");
            } else {
                await api.post("/admin/community-partners", partner);
                toast.success("Added.");
            }
            setEditing(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Save failed.");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/admin/community-partners/${p.id}`);
            toast.success("Deleted.");
            await load();
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    return (
        <section data-testid="admin-community-partners">
            <header className="flex items-start gap-4 justify-between flex-wrap mb-6">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl text-[#12776F]">Community partners</h1>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                        Spas, gyms, mental wellness, virtual doctors, meal services, respite — partners that help reduce caregiver burnout.
                        Live and Coming-soon entries appear at <Link to="/resources/community-partners" className="text-teal-dark underline">/resources/community-partners</Link>.
                        Drafts stay private to admins.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setEditing({ ...PARTNER_BLANK })}
                    data-testid="cp-add"
                    className="ml-btn-primary inline-flex items-center gap-2"
                >
                    <Plus size={16} strokeWidth={1.8} /> Add partner
                </button>
            </header>

            {subs.length > 0 && (
                <div className="ml-card p-5 bg-yellow-soft/30 border border-yellow-dark/20 mb-6" data-testid="mkt-approval-queue">
                    <p className="text-sm font-semibold text-[#1F2624]">Organization submissions awaiting approval ({subs.length})</p>
                    <ul className="mt-3 space-y-2.5">
                        {subs.map(s => (
                            <li key={s.id} className="flex items-start justify-between gap-3 flex-wrap bg-white border border-cream-dark rounded-2xl p-3.5" data-testid={`mkt-queue-${s.id}`}>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-[#1F2624]">
                                        {s.name} <span className="text-xs text-[#6E7A74]">· {s.kind} · {s.category}{s.region ? ` · ${s.region}` : ""}</span>
                                    </p>
                                    <p className="text-xs text-[#4C5551] mt-0.5">{s.description || "—"}{s.perk ? ` · Perk: ${s.perk}` : ""}{s.event_date ? ` · ${s.event_date}` : ""}</p>
                                    <p className="text-[11px] text-[#6E7A74] mt-0.5">From {s.org_name} ({s.submitted_by})</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button type="button" onClick={() => review(s.id, "approve")} data-testid={`mkt-approve-${s.id}`}
                                        className="px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-teal text-white hover:bg-teal-dark">Approve</button>
                                    <button type="button" onClick={() => review(s.id, "reject")} data-testid={`mkt-reject-${s.id}`}
                                        className="px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-coral/40 text-coral-dark hover:bg-coral-light/30">Reject</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </p>
            ) : items.length === 0 ? (
                <div className="ml-card p-6 bg-cream-light text-center">
                    <p className="text-sm text-[#4C5551]">No partners yet. Tap <strong>Add partner</strong> to seed the directory.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="cp-list">
                    {items.map(p => {
                        const catLabel = meta.categories.find(c => c.id === p.category)?.label || p.category;
                        const statusTone = p.status === "live"
                            ? "bg-stage_early/60 text-stage_early-dark"
                            : p.status === "coming_soon"
                                ? "bg-yellow-soft text-yellow-dark"
                                : "bg-cream-dark text-[#4C5551]";
                        return (
                            <li
                                key={p.id}
                                data-testid={`cp-row-${p.id}`}
                                className="ml-card p-4 md:p-5 bg-white flex items-start gap-3 justify-between"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-heading text-base text-[#1F2624]">{p.name}</span>
                                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl ${statusTone}`}>
                                            {p.status.replace("_", " ")}
                                        </span>
                                        <span className="text-xs text-[#6E7A74]">· {catLabel}</span>
                                        <span className="text-xs text-[#6E7A74] font-mono">· {p.country || "US"}</span>
                                        {p.region && <span className="text-xs text-[#6E7A74]">· {p.region}</span>}
                                    </div>
                                    {p.description && <p className="text-sm text-[#4C5551] mt-1 leading-relaxed line-clamp-2">{p.description}</p>}
                                    {p.perk && <p className="text-xs text-stage_early-dark mt-1">💛 {p.perk}</p>}
                                    {p.url && (
                                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1 mt-1">
                                            {p.url} <ExternalLink size={11} strokeWidth={1.8} />
                                        </a>
                                    )}
                                </div>
                                <div className="shrink-0 flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setEditing({ ...p })}
                                        data-testid={`cp-edit-${p.id}`}
                                        className="p-2 rounded-full text-teal-dark hover:bg-teal-soft"
                                        title="Edit"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => remove(p)}
                                        data-testid={`cp-delete-${p.id}`}
                                        className="p-2 rounded-full text-coral-dark hover:bg-coral-light/60"
                                        title="Delete"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {editing && (
                <PartnerEditModal
                    initial={editing}
                    meta={meta}
                    onClose={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </section>
    );
}

function PartnerEditModal({ initial, meta, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isNew = !initial.id;

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) {
            toast.error("Please enter a name.");
            return;
        }
        setSaving(true);
        await onSave(form);
        setSaving(false);
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="cp-modal"
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="ml-card bg-white max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto"
            >
                <h2 className="font-heading text-2xl text-[#12776F] mb-4">
                    {isNew ? "Add a community partner" : `Edit "${initial.name}"`}
                </h2>
                <div className="space-y-3">
                    <PartnerField label="Name" required>
                        <input
                            type="text" value={form.name} onChange={(e) => update("name", e.target.value)}
                            data-testid="cp-form-name" className="ml-input w-full" required
                        />
                    </PartnerField>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <PartnerField label="Category">
                            <select
                                value={form.category} onChange={(e) => update("category", e.target.value)}
                                data-testid="cp-form-category" className="ml-input w-full"
                            >
                                {meta.categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </PartnerField>
                        <PartnerField label="Status">
                            <select
                                value={form.status} onChange={(e) => update("status", e.target.value)}
                                data-testid="cp-form-status" className="ml-input w-full"
                            >
                                <option value="coming_soon">Coming soon</option>
                                <option value="live">Live now</option>
                                <option value="draft">Draft (admin only)</option>
                            </select>
                        </PartnerField>
                    </div>
                    <PartnerField label="Country">
                        <select
                            value={form.country || "US"}
                            onChange={(e) => update("country", e.target.value)}
                            data-testid="cp-form-country"
                            className="ml-input w-full"
                        >
                            <option value="US">🇺🇸 United States</option>
                            <option value="CA">🇨🇦 Canada</option>
                            <option value="MX">🇲🇽 Mexico</option>
                            <option value="GB">🇬🇧 United Kingdom</option>
                            <option value="IE">🇮🇪 Ireland</option>
                            <option value="AU">🇦🇺 Australia</option>
                            <option value="NZ">🇳🇿 New Zealand</option>
                            <option value="DE">🇩🇪 Germany</option>
                            <option value="FR">🇫🇷 France</option>
                            <option value="ES">🇪🇸 Spain</option>
                            <option value="GLOBAL">🌐 International (any country)</option>
                        </select>
                    </PartnerField>
                    <PartnerField label="Region (optional)">
                        <input
                            type="text" value={form.region || ""} onChange={(e) => update("region", e.target.value)}
                            placeholder="e.g. Houston, TX or Nationwide"
                            data-testid="cp-form-region" className="ml-input w-full"
                        />
                    </PartnerField>
                    <PartnerField label="Short description">
                        <textarea
                            value={form.description || ""} onChange={(e) => update("description", e.target.value)}
                            placeholder="One or two sentences caregivers will see."
                            data-testid="cp-form-description"
                            rows={3} maxLength={600}
                            className="ml-input w-full !rounded-2xl"
                        />
                    </PartnerField>
                    <PartnerField label="Caregiver perk (optional)">
                        <input
                            type="text" value={form.perk || ""} onChange={(e) => update("perk", e.target.value)}
                            placeholder='e.g. "20% off for Memory Lane caregivers"'
                            data-testid="cp-form-perk" className="ml-input w-full"
                            maxLength={200}
                        />
                    </PartnerField>
                    <PartnerField label="Website / link">
                        <input
                            type="url" value={form.url || ""} onChange={(e) => update("url", e.target.value)}
                            placeholder="https://"
                            data-testid="cp-form-url" className="ml-input w-full"
                        />
                    </PartnerField>
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button
                        type="button" onClick={onClose}
                        data-testid="cp-form-cancel"
                        className="px-5 py-2.5 rounded-2xl text-sm font-medium text-[#4C5551] hover:bg-cream-dark"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit" disabled={saving}
                        data-testid="cp-form-save"
                        className="ml-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <>Save</>}
                    </button>
                </div>
            </form>
        </div>
    );
}

function PartnerField({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">
                {label} {required && <span className="text-coral-dark">*</span>}
            </span>
            {children}
        </label>
    );
}

